import { MobileSearchWidget } from '@/components/search-widget/MobileSearchWidget';
import { RegularSearchWidget } from '@/components/search-widget/RegularSearchWidget';
import { brandColorsSelector } from '@/features/configuration/configurationSelector';
import { useSearchWidget } from '@/hooks/useSearchWidget';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';
import { TransAlert } from '@/i18n/trans/alert';
import { useSelector } from '@/store/utils';
import { breakpoints } from '@/utils/breakpoints';
import { Divider } from '@packages/webshop-design-system';
import {
  SearchWidget,
  type SearchFormValues,
} from '@packages/webshop-search-widget';
import { useEffect, useRef, type FC } from 'react';
import { useMediaQuery } from 'react-responsive';
import { toast } from 'react-toastify';

interface JourneyChangeSearchWidgetProps {
  /** Prefilled/last-searched form values seeding the widget's fields. */
  formValues: SearchFormValues | undefined;
  /** True while a re-search is in flight; disables the submit button. */
  isSearching: boolean;
  /** Runs a new token-scoped exchange search for the chosen route/date. */
  onSearch: (values: SearchFormValues) => void;
}

// Mirrors the manage-booking search UX (`SearchWidgetWrapper`) but drives its own
// token-scoped submit instead of the purchase-wired
// `SearchWidgetContext.handleSubmit`. The passenger set and product type are
// fixed by the exchange token and therefore locked; route and date stay editable.
export const JourneyChangeSearchWidget: FC<JourneyChangeSearchWidgetProps> = ({
  formValues,
  isSearching,
  onSearch,
}) => {
  const brandColors = useSelector(brandColorsSelector);
  const tenantConfiguration = useTenantConfiguration();
  const isLaptopOrBigger = useMediaQuery({ minWidth: breakpoints.laptop });
  const { setCurrentSearchFormValues, setIsSearchOverlayOpened } =
    useSearchWidget();

  // The initial search is fired by the hook, not the widget, so nothing has
  // populated the shared context the mobile trigger reads for its route/date
  // summary. Seed it once, so later user edits aren't overwritten.
  const hasSeededContext = useRef(false);
  useEffect(() => {
    if (formValues && !hasSeededContext.current) {
      hasSeededContext.current = true;
      setCurrentSearchFormValues(formValues);
    }
  }, [formValues, setCurrentSearchFormValues]);

  const handleSubmit = (values: SearchFormValues) => {
    setCurrentSearchFormValues(values);
    setIsSearchOverlayOpened(false);
    onSearch(values);
  };

  const handleValidationError = () => {
    toast.error(<TransAlert i18nKey="searchFormValidationFailed" />);
  };

  const Component = isLaptopOrBigger ? RegularSearchWidget : MobileSearchWidget;

  return (
    <div className="sticky top-0 z-50 bg-background">
      <Component>
        <SearchWidget
          baseUrl={import.meta.env.VITE_APP_PROXY_TARGET}
          onSubmit={handleSubmit}
          onValidationError={handleValidationError}
          height="small"
          lockedFields={{
            passengers: true,
            productType: true,
            submitButton: isSearching,
          }}
          brandColors={brandColors}
          backgroundTheme="light"
          stopsSearchingStrategy={tenantConfiguration?.stopsSearchingStrategy}
          formValues={formValues}
          defaultProductType="ticket"
        />
      </Component>
      <Divider variant="medium" />
    </div>
  );
};
